import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Award, FileText, Search, MessageSquare, ClipboardCheck, Images,
  Phone, Mail, ChevronRight, Accessibility, Scissors, Briefcase,
  PenTool, GraduationCap, Scale, TrendingUp, Users, ScrollText,
  ArrowRight, CheckCircle2, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useLangPrefix, withLangPrefix } from "../hooks/useLangPrefix";

const FORMATION_LINK = "/formation/236a04e2-2b27-45be-a24d-ea1f2744d182";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4">{children}</p>
);

const SectionTitle = ({ children, em }) => (
  <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight mb-6">
    {children} {em && <span className="font-serif italic text-[#C9B896]">{em}</span>}
  </h2>
);

const PREREQUIS_STEPS = [
  { icon: FileText, title: "Transmission d'un dossier de candidature", text: "Le candidat transmet a minima un curriculum vitae et une présentation de son projet professionnel. Ces éléments permettent d'identifier son parcours, ses expériences éventuelles en lien avec des activités manuelles ou techniques et la cohérence de son projet avec la certification." },
  { icon: Search, title: "Analyse du dossier", text: "Les éléments transmis sont étudiés afin de vérifier l'adéquation entre le profil du candidat et les exigences de la certification, notamment en termes de compréhension des activités visées et de projet professionnel." },
  { icon: MessageSquare, title: "Entretien individuel", text: "Un entretien est réalisé afin d'évaluer la capacité du candidat à comprendre des consignes en langue française, à expliciter son projet et à se projeter dans les situations professionnelles du métier." },
  { icon: ClipboardCheck, title: "Test de positionnement si nécessaire", text: "Un test peut être proposé afin de vérifier la maîtrise des compétences de base, notamment en compréhension écrite et en calcul." },
  { icon: Images, title: "Présentation de réalisations pour les candidats libres", text: "Dans le cas d'une candidature individuelle, le candidat présente a minima des réalisations sous forme de photographies, d'ouvrages ou de portfolio afin d'attester de son niveau technique." },
];

const BLOCS = [
  {
    num: "01",
    icon: PenTool,
    title: "Concevoir un projet de sellerie-garnissage",
    skills: [
      "Analyser un besoin client en sellerie-garnissage",
      "Concevoir une solution technique de sellerie-garnissage",
      "Sélectionner des matériaux et des procédés de fabrication",
      "Dimensionner techniquement un ouvrage de sellerie",
      "Formaliser une offre commerciale de sellerie-garnissage",
    ],
  },
  {
    num: "02",
    icon: Scissors,
    title: "Réaliser des ouvrages de sellerie-garnissage",
    skills: [
      "Organiser un poste de travail en atelier de sellerie",
      "Réaliser des gabarits d'ouvrages de sellerie-garnissage",
      "Préparer des éléments de fabrication en sellerie-garnissage",
      "Assembler des éléments par couture industrielle",
      "Réaliser le garnissage et la mise en forme d'un ouvrage de sellerie",
      "Installer un ouvrage de sellerie-garnissage",
    ],
  },
  {
    num: "03",
    icon: Briefcase,
    title: "Développer une activité d'entrepreneur en sellerie-garnissage",
    skills: [
      "Structurer un projet entrepreneurial en sellerie-garnissage",
      "Développer une stratégie commerciale en sellerie-garnissage",
      "Organiser la production d'une activité de sellerie-garnissage",
      "Piloter la gestion économique d'une activité de sellerie-garnissage",
      "Développer durablement une activité de sellerie-garnissage",
    ],
  },
];

const EVALUATIONS = [
  {
    bloc: "Bloc 1",
    title: "Concevoir un projet de sellerie-garnissage",
    intro: "Mise en situation professionnelle reconstituée, comprenant :",
    items: [
      "Séquence 1 — Entretien client et recueil du besoin",
      "Séquence 2 — Choix des matériaux et conception technique",
      "Séquence 3 — Élaboration du devis",
    ],
  },
  {
    bloc: "Bloc 2",
    title: "Réaliser des ouvrages de sellerie-garnissage",
    intro: "Épreuve 1 — Questionnaire technique individuel. Épreuve 2 — Mise en situation professionnelle reconstituée, comprenant :",
    items: [
      "Séquence 1 — Préparation du poste de travail",
      "Séquence 2 — Réalisation de la mousse",
      "Séquence 3 — Réalisation de la coiffe",
      "Séquence 4 — Contrôle final de l'ouvrage",
    ],
  },
  {
    bloc: "Bloc 3",
    title: "Développer une activité d'entrepreneur en sellerie-garnissage",
    intro: "",
    items: ["Épreuve — Présentation du projet professionnel et soutenance orale"],
  },
];

const INSCRIPTION_STEPS = [
  "Prise de contact pour échanger sur votre projet et vos besoins.",
  "Envoi du dossier d'inscription et des documents nécessaires.",
  "Entretien avec notre responsable pédagogique afin de vérifier que la formation correspond à votre objectif.",
  "Validation de l'inscription après réception des documents demandés et selon les places disponibles.",
  "Confirmation de votre entrée en formation et transmission des informations pratiques.",
];

export default function CertificationPage() {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  useEffect(() => {
    document.title = "Certification « Entrepreneur en sellerie-garnissage » | Sellerie Académie";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#FAF7F0]" data-testid="certification-page">
      {/* ───── HERO ───── */}
      <section className="bg-[#1F2A4A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-32 pb-20 md:pt-40 md:pb-28 relative">
          <div className="inline-flex items-center gap-2 border border-[#C9B896]/40 rounded-full px-4 py-1.5 mb-8" data-testid="certification-status-badge">
            <Clock className="w-3.5 h-3.5 text-[#C9B896]" />
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C9B896]">En cours d'examen par France Compétences</span>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#E8DCC8]/60 mb-5">Certification professionnelle</p>
          <h1 className="font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8 text-white">
            Entrepreneur en <span className="font-serif italic text-[#C9B896]">sellerie-garnissage</span>
          </h1>
          <p className="text-[#E8DCC8]/80 text-base md:text-lg max-w-2xl leading-relaxed mb-10">
            Concevoir, fabriquer et commercialiser des ouvrages de sellerie-garnissage, tout en développant une activité artisanale autonome et pérenne.
          </p>
          <Button asChild className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-6 sm:px-8 py-6 text-sm tracking-wide font-medium whitespace-normal h-auto max-w-full text-center" data-testid="certification-formation-cta">
            <Link to={L(FORMATION_LINK)}>
              Découvrir la formation qui prépare à la certification <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ───── OBJECTIFS ───── */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Eyebrow>Objectifs & contexte</Eyebrow>
        <SectionTitle em="de la certification">Objectifs et contexte</SectionTitle>
        <div className="grid md:grid-cols-2 gap-10 text-[#4A5568] leading-relaxed text-[15px]">
          <div className="space-y-5">
            <p>La certification « Entrepreneur en sellerie-garnissage » vise à certifier des professionnels capables de concevoir, fabriquer et commercialiser des ouvrages de sellerie-garnissage, tout en développant une activité artisanale autonome et pérenne.</p>
            <p>Elle atteste de la capacité à analyser un besoin client, formaliser une solution technique adaptée, conseiller, argumenter, réaliser des ouvrages sur mesure et en assurer l'installation dans le respect des exigences de qualité, de sécurité et de durabilité.</p>
            <p>Le titulaire intervient sur des supports variés tels que sièges complets, assises de coussin agrafé et amovible, bâches, housses, structures souples ou gonflables, en mobilisant des techniques de traçage, découpe, assemblage par couture, collage ou soudage, garnissage et de fixations, dans différents secteurs comme l'automobile, le nautisme, le motocycle, l'ameublement ou le médical.</p>
          </div>
          <div className="space-y-5">
            <p>La certification atteste également de la capacité à structurer et piloter une activité entrepreneuriale : positionnement sur un marché, développement commercial, organisation de la production et suivi de la rentabilité.</p>
            <p>Elle répond aux besoins du marché en professionnels polyvalents, capables d'allier maîtrise technique et gestion d'une activité artisanale dans un contexte de diversification des usages et de personnalisation des produits.</p>
            <div className="border-l-2 border-[#C9B896] pl-5 mt-6">
              <p className="font-serif italic text-[#1F2A4A] text-lg">« Allier maîtrise technique et gestion d'une activité artisanale. »</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── ACCÈS À LA CERTIFICATION ───── */}
      <section className="bg-[#F3EDE0] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Eyebrow>Voies d'accès</Eyebrow>
          <SectionTitle em="certification">Accès à la</SectionTitle>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-7 border border-[#1F2A4A]/8" data-testid="acces-formation-continue">
              <GraduationCap className="w-6 h-6 text-[#C9B896] mb-4" />
              <h3 className="text-[#1F2A4A] font-medium text-lg mb-3">Après un parcours de formation continue</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Après avoir suivi un parcours de formation continue auprès du certificateur ou d'un partenaire habilité, le candidat doit justifier de prérequis lui permettant d'entrer dans une démarche professionnalisante. Le candidat doit a minima être en capacité de comprendre des consignes écrites et orales en langue française et de réaliser des opérations de calcul de base. Une appétence pour les activités manuelles et techniques est attendue. Une première expérience, professionnelle ou personnelle, dans les domaines de l'artisanat, de la couture, du travail des matériaux souples ou d'activités manuelles constitue un atout, sans être obligatoire. L'accès à la formation est conditionné à un entretien ou à une analyse du projet du candidat permettant de vérifier l'adéquation entre son projet professionnel et les objectifs de la certification.</p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-[#1F2A4A]/8" data-testid="acces-vae">
              <ScrollText className="w-6 h-6 text-[#C9B896] mb-4" />
              <h3 className="text-[#1F2A4A] font-medium text-lg mb-3">Par la validation des acquis de l'expérience (VAE)</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Conformément à l'article R. 335-6 du code de l'éducation et à la loi n°2022-1598 portant mesures d'urgence relatives au fonctionnement du marché du travail en vue du plein emploi, l'accès à ce dispositif est ouvert à toute personne sans condition d'âge. Le candidat doit justifier d'une expérience en lien direct avec les activités visées par la certification, notamment dans les domaines de la sellerie-garnissage, de la fabrication d'ouvrages en matériaux souples, de la couture technique ou d'activités artisanales équivalentes.</p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-[#1F2A4A]/8" data-testid="acces-candidature-individuelle">
              <Users className="w-6 h-6 text-[#C9B896] mb-4" />
              <h3 className="text-[#1F2A4A] font-medium text-lg mb-3">En candidature individuelle</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Le candidat peut se présenter aux épreuves certificatives sans avoir suivi le parcours de formation. Il doit démontrer qu'il maîtrise les compétences visées par la certification. Le candidat doit a minima être en capacité de concevoir un projet de sellerie-garnissage, de réaliser un ouvrage en autonomie et de présenter un projet professionnel cohérent dans le domaine. Il doit également être en mesure de fournir des preuves de réalisation, telles que des ouvrages produits ou un portfolio, permettant d'attester de son niveau technique.</p>
            </div>
          </div>

          <h3 className="text-[#1F2A4A] font-light text-2xl mb-3">Vérification des prérequis <span className="font-serif italic text-[#C9B896]">en 5 étapes</span></h3>
          <p className="text-[#4A5568] text-sm mb-10 max-w-2xl">Les prérequis sont vérifiés en amont de l'entrée en formation ou de l'inscription aux épreuves certificatives selon les étapes suivantes :</p>
          <div className="space-y-4" data-testid="prerequis-steps">
            {PREREQUIS_STEPS.map((step, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#1F2A4A]/8 p-6 flex gap-5 items-start hover:shadow-md transition-shadow">
                <div className="shrink-0 w-11 h-11 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center font-serif italic text-lg">{i + 1}</div>
                <div>
                  <h4 className="text-[#1F2A4A] font-medium mb-1.5 flex items-center gap-2">
                    <step.icon className="w-4 h-4 text-[#C9B896]" /> {step.title}
                  </h4>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[#4A5568] text-sm mt-8 italic">L'ensemble de ces étapes permet de statuer sur l'adéquation du profil du candidat avec les exigences de la certification.</p>

          {/* Handicap */}
          <div className="mt-12 bg-[#1F2A4A] text-white rounded-2xl p-8 flex gap-5 items-start" data-testid="handicap-section">
            <Accessibility className="w-7 h-7 text-[#C9B896] shrink-0 mt-1" />
            <div>
              <h3 className="font-medium text-lg mb-2 text-white">Personne en situation de handicap</h3>
              <p className="text-[#E8DCC8]/80 text-sm leading-relaxed">Tout candidat peut saisir le certificateur pour aménager les modalités d'évaluation et obtenir, le cas échéant, un aménagement adapté à sa situation. Sur le conseil du référent handicap et dans le respect des spécifications du référentiel, les supports et le matériel nécessaires à la réalisation des évaluations pourront être adaptés en fonction du besoin.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── COMPÉTENCES ATTESTÉES ───── */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Eyebrow>Référentiel</Eyebrow>
        <SectionTitle em="attestées">Compétences</SectionTitle>
        <div className="grid md:grid-cols-3 gap-6" data-testid="blocs-competences">
          {BLOCS.map((bloc) => (
            <div key={bloc.num} className="bg-white rounded-2xl border border-[#1F2A4A]/8 p-7 relative overflow-hidden group hover:shadow-lg transition-shadow">
              <span className="absolute -top-4 -right-2 font-serif italic text-[90px] leading-none text-[#1F2A4A]/5 select-none">{bloc.num}</span>
              <bloc.icon className="w-6 h-6 text-[#C9B896] mb-4" />
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-2">Bloc {bloc.num}</p>
              <h3 className="text-[#1F2A4A] font-medium text-lg mb-5 leading-snug">{bloc.title}</h3>
              <ul className="space-y-2.5">
                {bloc.skills.map((s, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-sm text-[#4A5568] leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-[#C9B896] shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ───── MODALITÉS D'ÉVALUATION ───── */}
      <section className="bg-[#1F2A4A] text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Eyebrow>Épreuves</Eyebrow>
          <h2 className="font-light text-3xl md:text-4xl tracking-tight mb-12 text-white">
            Modalités <span className="font-serif italic text-[#C9B896]">d'évaluation</span>
          </h2>
          <div className="space-y-6" data-testid="modalites-evaluation">
            {EVALUATIONS.map((ev, i) => (
              <div key={i} className="border border-white/10 rounded-2xl p-7 bg-white/[0.03]">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-2">{ev.bloc}</p>
                <h3 className="font-medium text-lg mb-3 text-white">{ev.title}</h3>
                {ev.intro && <p className="text-[#E8DCC8]/70 text-sm mb-4">{ev.intro}</p>}
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  {ev.items.map((it, j) => (
                    <li key={j} className="flex gap-2.5 items-start text-sm text-[#E8DCC8]/85">
                      <ChevronRight className="w-4 h-4 text-[#C9B896] shrink-0 mt-0.5" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Délivrance + Jury */}
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            <div className="border border-white/10 rounded-2xl p-7 bg-white/[0.03]" data-testid="delivrance-section">
              <Award className="w-6 h-6 text-[#C9B896] mb-4" />
              <h3 className="font-medium text-lg mb-3 text-white">Délivrance de la certification</h3>
              <p className="text-[#E8DCC8]/75 text-sm leading-relaxed mb-3">La procédure de délivrance des certifications se structure autour de deux instances distinctes à la suite d'un parcours de formation : le jury d'évaluation et le jury de certification.</p>
              <p className="text-[#E8DCC8]/75 text-sm leading-relaxed mb-3">La mission principale du jury d'évaluation consiste à apprécier les performances des candidats au travers des épreuves écrites et orales. Il est également chargé de la compilation et de la transmission des résultats ainsi que des procès-verbaux relatifs à ces évaluations au certificateur.</p>
              <p className="text-[#E8DCC8]/75 text-sm leading-relaxed">Quant au jury de certification, son rôle est déterminant dans l'attribution finale de la certification. Il procède à une analyse rigoureuse pour s'assurer de la conformité des épreuves passées et des résultats obtenus par les candidats, en s'appuyant sur les documents fournis par le jury d'évaluation. C'est sur cette base que la décision d'octroyer ou non la certification est prise.</p>
            </div>
            <div className="border border-white/10 rounded-2xl p-7 bg-white/[0.03]" data-testid="jury-section">
              <Users className="w-6 h-6 text-[#C9B896] mb-4" />
              <h3 className="font-medium text-lg mb-3 text-white">Composition du jury d'évaluation / de certification</h3>
              <p className="text-[#E8DCC8]/75 text-sm leading-relaxed mb-6">Le jury d'évaluation et de certification est composé de deux professionnels exerçant dans le domaine de la sellerie-garnissage depuis au moins 1 an, ayant eu une expérience en qualité d'entrepreneur.</p>
              <h3 className="font-medium text-lg mb-3 text-white">Modalités de la certification</h3>
              <p className="text-[#E8DCC8]/75 text-sm leading-relaxed">La certification peut être acquise par capitalisation progressive des blocs de compétences. Sous réserve d'obtention de la certification, le candidat se voit remettre un parchemin.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── TAUX DE RÉUSSITE / SATISFACTION ───── */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Eyebrow>Résultats</Eyebrow>
        <SectionTitle em="& satisfaction">Taux de réussite</SectionTitle>
        <div className="grid md:grid-cols-3 gap-6 mb-10" data-testid="taux-reussite">
          <div className="bg-white rounded-2xl border border-[#1F2A4A]/8 p-8 text-center">
            <p className="font-serif italic text-6xl text-[#1F2A4A] mb-2">100<span className="text-3xl text-[#C9B896]">%</span></p>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-2">Taux de réussite global</p>
            <p className="text-sm text-[#4A5568]">8 stagiaires sur 8 ont réussi depuis le lancement de la certification.</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#1F2A4A]/8 p-8 text-center">
            <p className="font-serif italic text-6xl text-[#1F2A4A] mb-2">92<span className="text-3xl text-[#C9B896]">%</span></p>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-2">Taux de satisfaction</p>
            <p className="text-sm text-[#4A5568]">62,5 % des personnes certifiées en 2026 ont répondu à notre questionnaire de satisfaction. (Données au 10/2026)</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#1F2A4A]/8 p-8">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-4">Détail par année</p>
            <ul className="space-y-3 text-sm text-[#4A5568]">
              <li className="flex justify-between border-b border-[#1F2A4A]/8 pb-2"><span>2025</span><span className="text-[#1F2A4A] font-medium">100 % — 3 / 3</span></li>
              <li className="flex justify-between border-b border-[#1F2A4A]/8 pb-2"><span>2026</span><span className="text-[#1F2A4A] font-medium">100 % — 5 / 5</span></li>
              <li className="flex justify-between"><span>Global</span><span className="text-[#1F2A4A] font-medium">100 % — 8 / 8</span></li>
            </ul>
            <p className="text-xs text-[#4A5568]/70 mt-4 italic">Données actualisées en octobre 2026.</p>
          </div>
        </div>
        <div className="bg-[#F3EDE0] rounded-2xl p-7 flex gap-5 items-start" data-testid="impact-section">
          <TrendingUp className="w-6 h-6 text-[#C9B896] shrink-0 mt-1" />
          <div>
            <h3 className="text-[#1F2A4A] font-medium text-lg mb-2">Opportunités professionnelles et impact de la certification</h3>
            <p className="text-[#4A5568] text-sm leading-relaxed">6 mois et 2 ans après chaque passage de certification, Sellerie Académie lance une enquête auprès des personnes certifiées afin d'évaluer l'impact de sa certification sur leur carrière et leurs habitudes de travail.</p>
          </div>
        </div>
      </section>

      {/* ───── DÉBOUCHÉS / ÉQUIVALENCES ───── */}
      <section className="bg-[#F3EDE0] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10">
          <div data-testid="debouches-section">
            <Eyebrow>Et après ?</Eyebrow>
            <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] tracking-tight mb-5">Débouchés professionnels <span className="font-serif italic text-[#C9B896]">& poursuite d'études</span></h2>
            <div className="space-y-4 text-[#4A5568] text-sm leading-relaxed">
              <p>À l'issue de la formation, le principal débouché est la création ou le développement de sa propre activité en sellerie-garnissage, en tant qu'artisan ou entrepreneur indépendant.</p>
              <p>Le titulaire peut notamment intervenir dans les secteurs de la sellerie automobile, moto, nautique, ameublement ou restauration de mobilier, auprès de particuliers comme de professionnels.</p>
              <p>Il est également possible d'exercer comme salarié au sein d'un atelier de sellerie-garnissage ou d'une entreprise spécialisée.</p>
              <p>Des formations complémentaires peuvent ensuite permettre de se spécialiser dans certains domaines : automobile, nautisme, médical, habitat…</p>
            </div>
          </div>
          <div data-testid="equivalences-section">
            <Eyebrow>Passerelles</Eyebrow>
            <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] tracking-tight mb-5">Équivalences <span className="font-serif italic text-[#C9B896]">& passerelles</span></h2>
            <div className="space-y-4 text-[#4A5568] text-sm leading-relaxed">
              <p>À ce jour, aucune équivalence ou passerelle n'est prévue avec une autre certification.</p>
              <p>Les compétences acquises en sellerie-garnissage peuvent toutefois faciliter une poursuite de parcours vers des formations ou certifications complémentaires dans les domaines de la sellerie, de la couture, du garnissage, de l'automobile, du nautisme ou de l'ameublement.</p>
            </div>
            <div className="mt-8 bg-white rounded-2xl border border-[#1F2A4A]/8 p-6" data-testid="base-legale-section">
              <h3 className="text-[#1F2A4A] font-medium flex items-center gap-2 mb-2"><Scale className="w-4 h-4 text-[#C9B896]" /> Base légale</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed mb-3">Lien à venir — certification en cours d'examen par France Compétences.</p>
              <Link to={L(FORMATION_LINK)} className="text-sm text-[#1F2A4A] underline underline-offset-4 decoration-[#C9B896] hover:text-[#C9B896] transition-colors" data-testid="base-legale-formation-link">
                Cliquez ici pour découvrir la formation qui prépare à la certification
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───── INSCRIPTION ───── */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Eyebrow>Pour vous inscrire</Eyebrow>
        <SectionTitle em="votre certification ?">Comment préparer</SectionTitle>
        <p className="text-[#4A5568] text-sm mb-10 max-w-2xl">L'inscription à une formation de la Sellerie Académie se déroule en plusieurs étapes :</p>
        <div className="grid md:grid-cols-5 gap-4 mb-14" data-testid="inscription-steps">
          {INSCRIPTION_STEPS.map((txt, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#1F2A4A]/8 p-5 relative">
              <span className="font-serif italic text-4xl text-[#C9B896] block mb-3">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[#4A5568] text-[13px] leading-relaxed">{txt}</p>
            </div>
          ))}
        </div>

        {/* CTA contact */}
        <div className="bg-[#1F2A4A] rounded-3xl p-10 md:p-14 text-center relative overflow-hidden" data-testid="certification-contact-cta">
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4">Information · Inscription · Financement</p>
          <h3 className="text-white font-light text-2xl md:text-3xl mb-8">Contactez la <span className="font-serif italic text-[#C9B896]">Sellerie Académie</span></h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+33613038158" className="inline-flex items-center gap-2.5 bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-7 py-3.5 text-sm font-medium transition-colors" data-testid="certification-phone-link">
              <Phone className="w-4 h-4" /> +33 6 13 03 81 58
            </a>
            <a href="mailto:assistance.sellerie@gmail.com" className="inline-flex items-center gap-2.5 border border-[#C9B896]/50 text-[#E8DCC8] hover:bg-white/5 rounded-full px-7 py-3.5 text-sm font-medium transition-colors" data-testid="certification-email-link">
              <Mail className="w-4 h-4" /> assistance.sellerie@gmail.com
            </a>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10">
            <Button asChild variant="link" className="text-[#C9B896] hover:text-[#E0D4BA] whitespace-normal h-auto max-w-full text-center">
              <Link to={L(FORMATION_LINK)} data-testid="certification-bottom-formation-link">
                Découvrir la formation qui prépare à la certification <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
        <p className="text-center text-xs text-[#4A5568]/60 mt-10 font-mono tracking-wider">Page mise à jour le 02/10/2026</p>
      </section>
    </div>
  );
}
